import React, { useEffect, useState } from 'react';
import { Loader2, Save, Sparkles, Building2 } from 'lucide-react';
import { resetNewCustomerDaysCache } from '../CustomerBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerBadgeSettings({ token }) {
  const [days, setDays] = useState(60);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/customer-badges`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) { const j = await r.json(); setDays(j.new_customer_days ?? 60); }
      } finally { setLoading(false); }
    })();
  }, [token]);

  const save = async () => {
    setSaving(true); setMsg(''); setErr('');
    try {
      const r = await fetch(`${API_URL}/api/config/customer-badges`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ new_customer_days: Number(days) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Save failed');
      resetNewCustomerDaysCache();
      setMsg('Saved — reload the Orders/Customers view to see the new window applied.');
    } catch (e) {
      setErr(e.message || 'Network error');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 md:p-6 max-w-2xl" data-testid="customer-badge-settings">
      <h3 className="text-base md:text-lg font-bold text-slate-800">Customer badges</h3>
      <p className="text-sm text-slate-500 mt-1">
        Admin-only icons shown next to a customer name in Orders, Customers and the customer profile.
      </p>

      <div className="mt-4 space-y-2 text-sm text-slate-700">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center ring-1 ring-emerald-300/70"><Sparkles className="w-3 h-3" /></span>
          New customer — registered within the window below
        </div>
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-blue-600 text-white inline-flex items-center justify-center ring-1 ring-blue-300/70"><Building2 className="w-3 h-3" /></span>
          Company account — registered as a company
        </div>
      </div>

      <label className="block mt-5 text-[11px] font-bold text-slate-500 uppercase tracking-wide">New-customer window (days)</label>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="number"
          min={1}
          max={3650}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          data-testid="customer-badge-new-days"
          className="w-32 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />
        <button
          onClick={save}
          disabled={saving}
          data-testid="customer-badge-save"
          className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold inline-flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
      {msg && <p className="mt-3 text-sm text-emerald-700" data-testid="customer-badge-msg">{msg}</p>}
      {err && <p className="mt-3 text-sm text-red-600" data-testid="customer-badge-err">{err}</p>}
    </div>
  );
}
